import { ForwardedRef, forwardRef, Ref, useImperativeHandle } from 'react';
import classnames from 'classnames';
import classNames from 'classnames';

import { FormDialogPopover } from '@xemplo/form-dialog-popover';
import { DawnArrowDown } from '@xemplo/icons';
import {
  InputFieldSize,
  LabelHelperText,
  MaybeErrorOrDescription,
} from '@xemplo/input-utils';
import { ListBox, ListItem } from '@xemplo/listbox';

import { SingleValue } from '../single-value/single-value';
import { Tags } from '../tags/tags';

import * as S from './text-dropdown-multi-v2.style';
import {
  BaseTextDropdownMultiProps,
  SelectRefCallback,
} from './text-dropdown-multi-v2.types';
import { useDropdownMulti } from './use-dropdown-multi';

/** Some other libs are using this to target the select, so keeping this for now */
export const TextDropdownMultiV2TestId = {
  select: (testId?: string) => `dropdown-multi-select-${testId}`,
};

/**
 * Base dropdown multi that can be used without a form
 * This is using the base as a name prefix because the original
 * TextDropdownMultiV2 is already being used in multiple locations
 * and it should be TextDropdownMultiRhf. To avoid refactoring
 * everything, we are using the base prefix for now.
 */
const BaseTextDropdownMultiInternal = <T extends ListItem>(
  props: Readonly<BaseTextDropdownMultiProps<T>>,
  ref?: ForwardedRef<SelectRefCallback>
) => {
  const {
    className,
    displayProps,
    error,
    invalid,
    isDropdownMulti,
    items,
    name,
    selectProps,
    testId,
    value,
    enableSearch,
  } = props;

  const { placeholder, label } = selectProps ?? {};

  const {
    description,
    inputSize = InputFieldSize.Standard,
    leadingIcon,
    width,
  } = displayProps ?? {};

  const {
    arrowIconStyle,
    containerStyle,
    labelStyle,
    placeholderStyle,
    selectStyle,
    containerRef,
    listRef,
    selectRef,
    singleValueRef,
    canShowOptionalLabel,
    multiState,
    selectionMode,
    selectionStyle,
  } = useDropdownMulti(props);

  const {
    popoverState,
    listState,
    searchState,
    selectPropsInternal,
    handleContainerClick,
  } = multiState;
  const showPlaceholder =
    value === null || value === undefined || (Array.isArray(value) && !value.length);

  // NOTE: We can't just pass the entire selection manager as the
  // react-aria class makes extensive usage of `this` and it will
  // break the context. So we need to provide only what we need
  // so the bindings are correctly set.
  useImperativeHandle(
    ref,
    () => ({
      clearSelection: () => listState.selectionManager.clearSelection(),
      setSelectedKeys: (keys) => listState.selectionManager.setSelectedKeys(keys),
      get selectedKeys() {
        return listState.selectionManager.selectedKeys;
      },
    }),
    [listState.selectionManager]
  );

  return (
    <S.Wrapper className={className} $width={width}>
      <S.Container
        ref={containerRef}
        $inputSize={inputSize}
        role="combobox"
        onClick={handleContainerClick}
        className={classnames([inputSize], containerStyle)}
      >
        {leadingIcon && (
          <S.LeadingIcon className={classnames([inputSize])}>{leadingIcon}</S.LeadingIcon>
        )}
        {placeholder && showPlaceholder && (
          <S.Placeholder className={classnames(placeholderStyle)}>
            {placeholder}
          </S.Placeholder>
        )}
        <S.Label className={classnames(labelStyle)}>
          {inputSize === InputFieldSize.Large && label}
          {canShowOptionalLabel && (
            <LabelHelperText className="label-helper-text">(optional)</LabelHelperText>
          )}
        </S.Label>
        <S.Select
          {...selectPropsInternal}
          ref={selectRef}
          role="listbox"
          className={classnames([inputSize], selectStyle)}
          data-testid={TextDropdownMultiV2TestId.select(testId)}
        >
          {items.map((item) => (
            <option key={item.key} value={item.key} hidden>
              {item.value}
            </option>
          ))}
        </S.Select>
        {isDropdownMulti ? (
          <Tags
            state={listState}
            hasLeadingIcon={!!leadingIcon}
            testId={testId}
            items={items}
            hasSiblings={!!(inputSize === InputFieldSize.Large && label)}
          />
        ) : (
          <SingleValue
            state={listState}
            hasLeadingIcon={!!leadingIcon}
            testId={testId}
            items={items}
            popoverState={popoverState}
            ref={singleValueRef}
            {...searchState}
            hasSiblings={!!(inputSize === InputFieldSize.Large && label)}
            className={classNames({
              'is-disabled': displayProps?.isReadOnly || displayProps?.isDisabled,
            })}
          />
        )}
        <S.ArrowIcon
          className={classnames(arrowIconStyle)}
          aria-label={`Toggle dropdown for ${label ?? 'dropdown'}`}
        >
          <DawnArrowDown fontSize={16} />
        </S.ArrowIcon>
      </S.Container>
      <MaybeErrorOrDescription
        error={invalid}
        errorMessage={error}
        description={description}
      />
      {popoverState.isOpen && (
        <FormDialogPopover
          state={popoverState}
          triggerRef={containerRef}
          containerPadding={-10}
        >
          <ListBox
            ref={listRef}
            items={items}
            state={listState}
            aria-label={label}
            aria-labelledby={`${name}-label`}
            selectionStyle={selectionStyle}
            selectionMode={selectionMode}
            omitSearch={props.isDropdownMulti && props.omitSearch}
            omitSelectAll={props.isDropdownMulti && props.omitSelectAll}
            width={`${containerRef.current?.clientWidth ?? 0}px`}
            testId={testId}
            enableSearch={enableSearch}
            {...searchState}
          />
        </FormDialogPopover>
      )}
    </S.Wrapper>
  );
};

export const BaseTextDropdownMulti = forwardRef(BaseTextDropdownMultiInternal) as <
  T extends ListItem
>(
  props: BaseTextDropdownMultiProps<T> & { ref?: Ref<SelectRefCallback> }
) => ReturnType<typeof BaseTextDropdownMultiInternal>;
